// @ts-strict-ignore
import cloneDeep from 'lodash/cloneDeep';
import {Component, ConfigOptions} from 'panel';
import queryString from 'query-string';

import {setTrackingCookies} from 'media/js/static/landing/marketing/cookie-tracker';
import {apiClient} from 'common/util/api-client';

import {track, TrackEvents, TrackProperties} from '../tracking';
import {template} from './template';
import {FormValues, SignupState} from './types';
import './index.styl';

export class Signup extends Component<SignupState> {
  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      'is-uber': `boolean`,
      'guest-id': {type: `string`, description: `id for guest user from server`},
      'is-gdpr': {
        type: `boolean`,
        description: `should show checkbox to manually opt-in marketing email for GDPR countries`,
      },
      'already-invited-error': {
        type: `json`,
        description: `additional error state from server, including SSO related issue`,
      },
      'users-preview': {
        type: `json`,
        tsType: `MixpanelUserItem[]`,
        description: `array with users that already belongs to the org`,
      },
      'users-total': {
        type: `number`,
        description: `total of users in the organization`,
      },
      next: {type: `string`, description: `next url`},
      'social-credential': {
        type: `string`,
        description: `Social authentication token, set if user attempted social login before sign-up`,
      },
      'enable-google-sign-in': {type: `boolean`, description: `whether or not to show sign-in with google button`},
      'enable-magic-link-registration': {type: `boolean`, description: `whether or not to use magic link registration`},
    };
  }
  get config(): ConfigOptions<SignupState> {
    return {
      template,
      defaultState: {
        defaultFormValues: {
          name: ``,
          email: ``,
          password: ``,
          socialCredential: ``,
          enableMagicLinkRegistration: false,
          company: ``,
          phone: null,
          numberOfEmployees: null,
          numberOfMaus: null,
          agreedToTermsOfUse: false,
          agreedToEmailPromo: false,
          department: null,
        },
        promoSlug: null,
        next: ``,
        isInvited: false,
        inviter: ``,
        incubator: ``,
        creditStartup: false,
        alreadyInvitedError: null,
        attemptingNonceValidation: false,
      },
      helpers: {
        getSignupFormAttributes: () => {
          return {
            ...this.getCommonFormAttributes(),
            incubator: this.state.incubator,
            'credit-startup': this.state.creditStartup,
            'guest-id': this.attr(`guest-id`),
          };
        },
        getInvitationFormAttributes: () => {
          return {
            ...this.getCommonFormAttributes(),
            inviter: this.state.inviter,
            'users-preview': this.attr(`users-preview`),
            'users-total': this.attr(`users-total`),
            'is-uber': this.attr(`is-uber`),
          };
        },
        handleAlreadyInvitedError: ({detail}) => {
          this.update({
            alreadyInvitedError: detail.error,
          });
        },
        handleSignUpSuccess: (ev) => {
          this.dispatchEvent(new CustomEvent(`signUpSuccess`, ev));
        },
      },
    };
  }

  getCommonFormAttributes() {
    const {promoSlug, next} = this.state;
    return {
      'already-invited-error': this.attr(`already-invited-error`),
      next,
      'is-gdpr': this.attr(`is-gdpr`),
      'promo-slug': promoSlug,
      'enable-magic-link-registration': true,
    };
  }

  async connectedCallback() {
    super.connectedCallback();

    setTrackingCookies();
    const queryParams = queryString.parse(window.location.search);
    if (queryParams.nonce_id) {
      this.update({attemptingNonceValidation: true});
      await apiClient.post(`/validate_email/${queryParams.nonce_id}/`, {});
      this.update({attemptingNonceValidation: false});
    }
    const isInvited = queryParams.invited === `1`;
    const isDemo = queryParams.demo === `1`;
    const stateUpdate: Partial<SignupState> = {isInvited};
    const formValuesUpdate: FormValues = cloneDeep(this.state.defaultFormValues);
    formValuesUpdate.enableMagicLinkRegistration = true;
    const socialCredential = this.attr(`social-credential`);
    const extraTrackingParams = {};
    if (socialCredential) {
      // Redirected from /login, user attempted social login before signup.
      formValuesUpdate.socialCredential = socialCredential;
    }
    // When it's invitation, name email and company come as part of query params
    if (isInvited) {
      formValuesUpdate.name = queryParams.name;
      formValuesUpdate.email = queryParams.email;
      formValuesUpdate.company = queryParams.company;
      stateUpdate.inviter = queryParams.inviter;
    } else if (isDemo) {
      // When it's via demo project request access we have name and email
      formValuesUpdate.name = queryParams.name;
      formValuesUpdate.email = queryParams.email;
    }
    if (queryParams.next) {
      stateUpdate.next = queryParams.next;
      // track mp_source for viewing the sign up page
      if (stateUpdate.next.includes(`mp_source=custom_alert`)) {
        extraTrackingParams[`mp_source`] = `custom_alert`;
      }
      if (stateUpdate.next.includes(`mp_source=anomaly_detection_alert`)) {
        extraTrackingParams[`mp_source`] = `anomaly_detection_alert`;
      }
      if (!stateUpdate.next.includes(`#`) && window.location?.hash) {
        stateUpdate.next += window.location.hash;
      }
      // replace mutiple "##" with single "#"; remove trailing "#"
      stateUpdate.next = stateUpdate.next.replace(/#+/g, `#`).replace(/#$/, ``);
    }
    if (queryParams.promoSlug) {
      stateUpdate.promoSlug = queryParams.promoSlug;
    }
    if (queryParams.incubator) {
      stateUpdate.incubator = queryParams.incubator;
    }
    if (queryParams.creditStartup) {
      stateUpdate.creditStartup = true;
    }

    this.update({
      ...stateUpdate,
      defaultFormValues: formValuesUpdate,
    });
    track(TrackEvents.ViewSignupPage, {
      [TrackProperties.PageVersion]: `Signup Page`,
      [TrackProperties.IsInvited]: this.state.isInvited,
      [TrackProperties.IsDemo]: isDemo,
      [TrackProperties.IsMagicLinkRegistration]: true,
      ...extraTrackingParams,
    });
  }
}

customElements.define(`mp-signup`, Signup);
